import { CashoutHubStore, OfferHubStore } from "@state/hubs";

import { httpClient, getAgencyKey, getApplicationHeaders } from "@utils";

import { LocalStorageProvider, StorageStateKeysEnum } from "@utils/storage";
import { action, observable, computed, runInAction } from "mobx";
import { QRCodeScannerStore, ThemeStore } from "./components/subheader";

import { RestrictionEnum } from "@api-types/restriction/RestrictionEnum";
import {
	AutomaticLogoutStore,
	AccountVerificationStore,
} from "@state/stores/account-settings";
import { SportStatisticHubStore } from "@state/hubs/SportStatisticsHubStore";
import { isCapacitorPlatform } from "@utils/specific/capacitor";
import { ChatViewStore } from "./chat/ChatViewStore";
import { NotificationFavoritesStore } from "./notification-favorites/NotificationFavoritesStore";
import { NotificationSettingsStore } from "./notification-settings/NotificationSettingsStore";

import { NameProvider } from "@gp/utility";
import { BetBuilderStore } from "@gp/slip";
import { BetBuilderService } from "@gp/hub";

import { SessionStorageProvider } from "@state/providers";
import BetSlipStore from "@state/bet-slip/BetSlipStore";
import { BaseRootStore } from "./BaseRootStore";
import { IStoreLifeCycle } from "@interface";
import { DiagnosticsProvider } from "@state/providers/diagnosticsProvider";
import { BetBoosterHubStore } from "@state/hubs/BetBoosterHubStore";
import { XtremepushNotificationPermissionPopupStore } from "./crm";
import PageStore from "@state/stores/common/PageStore";
import AppPopupStore from "./popups/AppPopupStore";
import BasePopupStore from "./popups/BasePopupStore";
import OnboardingWelcomePopupStore from "./popups/OnboardingWelcomePopupStore";
import BetBuilderPopupStore from "@state/bet-builder/BetBuilderPopupStore";
import GamePauseStore from "@state/shared/GamePauseStore";
import BankIdCapacitorStore from "@state/shared/BankIdCapacitorStore";
import {
	CapacitorUpdaterStore,
	RealityCheckStore,
	RequiredUserActionsStore,
	SessionExpireStore,
	User24hLockStore,
	UserAuthStore,
	WelcomeStore,
} from "@state/shared";
import { QuickPlayStore } from "@state/quick-play-store";
import CountryChangeStore from "@state/shared/CountryChangeStore";
import SearchStore from "./offer/search/SearchStore";
import { logger } from "@utils/logger/logger";
import CasinoSearchStore from "./games/CasinoSearchStore";
import GracePeriodStore from "@state/shared/GracePeriodStore";
import RetryHandlerStore from "@state/shared/RetryHandlerStore";
import PersonalDataStore from "./personal-data/PersonalDataStore";
import LogoutPopupStore from "@state/shared/LogoutPopupStore";
import WaitingPeriodStore from "@state/shared/WaitingPeriodStore";

class RootStore extends BaseRootStore implements IStoreLifeCycle {
	platform: "mobile" = "mobile";

	appPopupStore: AppPopupStore;
	favoritePopupStore: BasePopupStore;
	nameProvider: NameProvider;
	qRCodeScannerStore: QRCodeScannerStore;
	gamePauseStore: GamePauseStore;
	bankIdCapacitorStore: BankIdCapacitorStore;
	pageStore: PageStore;
	casinoSearchStore: CasinoSearchStore;

	@observable isUserLoginHistoryPopupActive = false;
	@observable isMultiSelectActive = false;

	constructor() {
		super();
		this.localStorageProvider = new LocalStorageProvider();
		this.userAuthStore = new UserAuthStore(this);
		this.diagnosticsProvider = new DiagnosticsProvider(this.userAuthStore);

		//@ts-expect-error
		this.qRCodeScannerStore = new QRCodeScannerStore(this);

		this.themeStore = new ThemeStore(this);

		this.automaticLogoutStore = new AutomaticLogoutStore(this);

		this.realityCheckStore = new RealityCheckStore(this);

		this.sessionExpireStore = new SessionExpireStore(this);

		this.user24hLockStore = new User24hLockStore(this);
		this.userAuthStore.syncToken();
		// needs to happen because of possible wrong data formatting in acc key in local storage !!!!
		// can be removed after some time but doesn't hurt
		this.userAuthStore.checkBalanceEntry();

		this.appPopupStore = new AppPopupStore(this);

		this.offerHub = new OfferHubStore(this.userAuthStore);
		this.betBoosterHubStore = new BetBoosterHubStore(this.userAuthStore);

		this.cashoutHubStore = new CashoutHubStore(this);
		this.accountVerificationStore = new AccountVerificationStore();

		this.favoritePopupStore = new BasePopupStore(this);

		this.welcomeStore = new WelcomeStore(this);
		this.requiredUserActionStore = new RequiredUserActionsStore(
			this.userAuthStore
		);

		this.capacitorUpdaterStore = new CapacitorUpdaterStore(this);
		// used for requestId query param
		this.onboardingWelcomePopupStore = new OnboardingWelcomePopupStore(
			this
		);

		this.quickPlayStore = new QuickPlayStore(this);
		this.chatViewStore = new ChatViewStore(this);
		//@ts-expect-error
		this.gamePauseStore = new GamePauseStore(this);

		this.sportStatisticsHub = new SportStatisticHubStore(
			this.userAuthStore
		);

		this.notificationFavoritesStore = new NotificationFavoritesStore(
			this.userAuthStore
		);
		if (isCapacitorPlatform()) {
			this.notificationSettingsStore = new NotificationSettingsStore();
		}

		this.nameProvider = new NameProvider();

		this.betSlipStore = new BetSlipStore({
			offerHub: this.offerHub.hub,
			userStore: this.userAuthStore,
			notificationStore: this.notificationStore,
			rootStore: this,
			balanceResolver: () => this.userAuthStore.resolveBalance(),
		});

		this.betBuilderPopupStore = new BetBuilderPopupStore(this);
		this.betBuilderStore = new BetBuilderStore({
			isNextOddsDisabled: true,
			betSlipStore: this.betSlipStore.betSlipState,
			additionalOfferStores: [],
			betBuilderService: new BetBuilderService({
				apiClient: {
					get: async (relativeUrl: string) => {
						const url = `${WEBPACK_BASE_PROXY_ADDRESS}${getAgencyKey()}/${relativeUrl}`;
						const response = await httpClient.get(
							url,
							// @ts-expect-error
							() => {
								const appHeaders = getApplicationHeaders();
								// @ts-expect-error
								appHeaders["Content-Type"] = "application/json";
								return appHeaders;
							}
						);
						return await response.json();
					},
				},
				callerEnvironment: WEBPACK_ENVIRONMENT_KEY,
			}),
			addRemoveErrorHandler: this.betSlipStore.addRemoveErrorHandler,
		});

		//@ts-expect-error
		this.countryChangeStore = new CountryChangeStore(this);

		//@ts-expect-error
		this.bankIdCapacitorStore = new BankIdCapacitorStore(this);
		this.pageStore = new PageStore(this);

		this.xtremepushNotificationPermissionPopupStore =
			new XtremepushNotificationPermissionPopupStore(this);

		this.searchStore = new SearchStore(this);

		this.casinoSearchStore = new CasinoSearchStore(this);

		//@ts-expect-error
		this.gracePeriodStore = new GracePeriodStore(this);

		//@ts-expect-error
		this.retryHandlerStore = new RetryHandlerStore(this);

		//@ts-expect-error
		this.personalDataStore = new PersonalDataStore(this);
		
		//@ts-expect-error
		this.waitingPeriodStore = new WaitingPeriodStore(this);

		this.logoutPopupStore = new LogoutPopupStore(this);
	}

	@computed get isRestrictionTypeDeny() {
		return this.restrictionType === RestrictionEnum.Deny;
	}

	@action.bound
	async onInitialize() {
		// need to fetch local storage values,
		// all other stores need to consume local storage after fetching
		await this.localStorageProvider.onInitialize();
		runInAction(() => {
			this.setLocalStorageLoadStatus(true);
			this.betSlipStore.onInitialize();
			this.themeStore.setInitialValue();

			this.paymentOptionsStore.onStoreInitialize();
			this.userAuthStore.onInitialize();
			this.realityCheckStore.initializeRealityCheck();
			this.cashoutHubStore.initializeCashoutHub();
			this.sessionExpireStore.onInitialize();
			this.welcomeStore.onInitialize();
			this.appPopupStore.onInitialize();

			this.onboardingWelcomePopupStore.onInitialize();
			this.notificationFavoritesStore.getUserNotificationFavorites();

			// used for requestId query param
			this.uniqueId = Date.now();
			this.chatViewStore.updateChatOnLoginChange(
				this.userAuthStore.isLoggedIn
			);
			this.xtremepushNotificationPermissionPopupStore.onInitialize();
			this.checkIfIpRestricted();
			this.isStoreInitialized = true;
			const parsedMultiSelectArg = JSON.parse(
				SessionStorageProvider.get(
					StorageStateKeysEnum.IS_MULTI_SELECT_ACTIVE
				) || "0"
			);
			this.isMultiSelectActive = parsedMultiSelectArg == "1";
			this.maintenanceStore.onInitialize();
			logger.setUserContext(this.userAuthStore);
			SessionStorageProvider.set(
				StorageStateKeysEnum.PREMATCH_MENU_VERSION,
				"1"
			);
		});
	}

	@action.bound
	enableMultiSelect() {
		this.isMultiSelectActive = true;

		SessionStorageProvider.set(
			StorageStateKeysEnum.IS_MULTI_SELECT_ACTIVE,
			"1"
		);
	}

	@action.bound
	disableMultiSelect() {
		this.isMultiSelectActive = false;
		SessionStorageProvider.set(
			StorageStateKeysEnum.IS_MULTI_SELECT_ACTIVE,
			"0"
		);
	}

	@action.bound
	closePopups() {
		this.realityCheckStore.deactivatePopup();
		this.automaticLogoutStore.deactivatePopup();
		this.accountVerificationStore.closePopup();
	}

	@action.bound
	resetPopups() {
		this.isDepositLimitUpdatePopupActive = false;
	}

	@action.bound
	onDispose() {
		this.isStoreInitialized = false;
		this.paymentOptionsStore.onDispose();
		this.userAuthStore.onDispose();
		this.capacitorUpdaterStore.onDispose();
		this.betSlipStore.onDispose();
	}
}

export default RootStore;
